import { action, computed, makeObservable, observable } from 'mobx';

import { AsyncCallback, FileAccessError, MFAError } from '@/types/types';
import { captureErrorForSentry, isUnionType, getErrorResponse } from '@/components/utils';
import { OpenOptions } from '@/config/openOptions';

import AuthSettingsStore from '../AuthSettingsStore';
import BatchDownloadStore from '../BatchDownloadStore';
import {
    AccessMap,
    AccessType,
    AccessTypeState,
    BatchDownloadState,
    DownloadActionResult,
    FetchFileArgs,
    FileAccessState,
    FullAccessState,
    StatePartial,
    SuccessAccessResponse,
} from './interfaces';
import { BATCH_DOWNLOAD_KEY, getErrorType, getOpenFileErrorText, MFA_ERRORS, MFA_STATE_INIT, openFile } from './helpers';
import i18n from '@/content';

export {
    openFile,
    MAP_ERROR_TYPE_TO_STATUS_CODE,
    checkIsMFAErrorType,
    getOpenFileErrorText,
} from './helpers';

export type { AccessType, FullAccessState, AccessTypeState };

class FilesAccessStore {
    constructor(authSettingsStore: AuthSettingsStore, batchDownloadStore: BatchDownloadStore) {
        this.authSettingsStore = authSettingsStore;
        this.batchDownloadStore = batchDownloadStore;
        makeObservable(this);
    }

    @observable accessMap: AccessMap = new Map<string, FileAccessState>();

    @observable selectedAccessFileId: string;

    @observable protectedFileInfo: FetchFileArgs;

    private readonly authSettingsStore: AuthSettingsStore;

    private readonly batchDownloadStore: BatchDownloadStore;

    private pollingIntervals: Map<string, NodeJS.Timeout> = new Map();

    @computed
    get batchDownloadState(): BatchDownloadState {
        const state = this.accessMap.get(BATCH_DOWNLOAD_KEY);
        if (state) {
            const { MFAState, accessTypesState: { batchDownload } } = state;
            return { MFAState, batchDownload };
        }
        return null;
    }

    @computed
    get isBatchDownloadLoading(): boolean {
        return this.batchDownloadState?.batchDownload?.isLoading;
    }

    @computed
    get batchDownloadError(): FileAccessError {
        return this.batchDownloadState?.batchDownload?.errorType;
    }

    @computed
    get batchDownloadAttemptsCounter(): number {
        return this.batchDownloadState?.MFAState?.attemptsCounter;
    }

    @computed
    get selectedItemState(): FileAccessState {
        return this.accessMap.get(this.selectedAccessFileId);
    }

    @computed
    get selectedItemAccessTypesState(): FullAccessState {
        return this.accessMap.get(this.selectedAccessFileId)?.accessTypesState;
    }

    @computed
    get selectedItemAttemptsCounter(): number {
        return this.selectedItemState?.MFAState?.attemptsCounter;
    }

    @computed
    get selectedItemMFAError(): MFAError {
        return this.selectedItemState?.MFAState?.errorType;
    }

    /**
     * Remembers the last context where backend responded with
     * `MFAPasswordRequired` so that MFA password modal can decide
     * how to correctly re-trigger the `/open` request (viewer,
     * office365/googledrive, or download).
     */
    @observable
    lastMFAPasswordContext: { key: string; accessType: AccessType } | null = null;

    @action
    setAccessItem = (key: string, state: FileAccessState): void => {
        this.accessMap.set(key, state);
    };

    @action
    deleteAccessItem = (key: string): void => {
        this.accessMap.delete(key);
    };

    @action
    setSelectedAccessFileId = (value: string): void => {
        this.selectedAccessFileId = value;
    };

    @action
    setLastMFAPasswordContext = (value: { key: string; accessType: AccessType } | null): void => {
        this.lastMFAPasswordContext = value;
    };

    @action
    setProtectedFileInfo = (value: FetchFileArgs): void => {
        this.protectedFileInfo = value;
    };

    @action
    clearSelectedItem = (): void => {
        this.clearPollingInterval(this.selectedAccessFileId);
        this.accessMap.delete(this.selectedAccessFileId);
        this.selectedAccessFileId = null;
    };

    private clearPollingInterval = (fileId: string): void => {
        const interval = this.pollingIntervals.get(fileId);
        if (interval) {
            clearInterval(interval);
            this.pollingIntervals.delete(fileId);
        }
    };

    getAccessTypeState = <T extends AccessType>(fileId: string, accessType: T): AccessTypeState<T> => (
        this.accessMap.get(fileId)?.accessTypesState?.[accessType] as AccessTypeState<T>
    );

    checkIsLoading = (fileId: string, accessType: AccessType): boolean => (
        this.accessMap.get(fileId)?.accessTypesState?.[accessType]?.isLoading
    );

    retryOpenFileRequestForWidget = () => {
        return this.tryFetchAccessLink({ ...this.protectedFileInfo, isProtected: true });
    };

    /**
     * Silent background retry for the 408 "Still securing file" screen. Performs a
     * single non-blocking viewer open and only updates state on success or a
     * non-408 failure — never flips to a loading/spinner state while the file is
     * still being prepared, so the user does not see a "Preparing file" flash.
     */
    silentRetryViewerOpen = async (fileId: string, alwaysCheckMFA?: boolean): Promise<void> => {
        try {
            const { API } = this.authSettingsStore;
            const result = await openFile({ API, accessType: OpenOptions.viewer, fileId, alwaysCheckMFA });
            const { status } = result as SuccessAccessResponse;
            const ready = status === 'success' || !!(result as { viewUrl?: string }).viewUrl
                || !!(result as { link?: string }).link;
            if (ready) {
                this.updateFileAccessState(fileId, OpenOptions.viewer, {
                    MFAStatePartial: { ...MFA_STATE_INIT },
                    newAccessTypeState: { result, isLoading: false },
                });
            }
        } catch (error) {
            const errorType = getErrorType(error);
            if (errorType !== 'fileBeingPrepared') {
                this.updateFileAccessState(fileId, OpenOptions.viewer, {
                    newAccessTypeState: { errorType, isLoading: false },
                });
            }
        }
    };

    private startViewerPolling = (
        fileId: string,
        filename: string,
        accessType: OpenOptions,
        alwaysCheckMFA?: boolean,
    ): void => {
        const MAX_POLL_ATTEMPTS = 30;
        let pollAttempts = 0;

        this.clearPollingInterval(fileId);

        const currentViewerState = this.accessMap.get(fileId)?.accessTypesState?.[accessType];
        const preservedResult = currentViewerState?.result;
        this.updateFileAccessState(fileId, accessType, {
            MFAStatePartial: { ...MFA_STATE_INIT },
            newAccessTypeState: {
                ...(currentViewerState && { ...currentViewerState }),
                ...(preservedResult && { result: preservedResult }),
                isLoading: true,
            },
        });

        const pollInterval = setInterval(async () => {
            pollAttempts += 1;

            if (pollAttempts > MAX_POLL_ATTEMPTS) {
                this.clearPollingInterval(fileId);
                this.updateFileAccessState(fileId, accessType, {
                    MFAStatePartial: { ...MFA_STATE_INIT },
                    newAccessTypeState: { errorType: 'fileBeingPrepared', isLoading: false },
                });
                return;
            }

            try {
                const { API } = this.authSettingsStore;
                const result = await openFile({
                    API,
                    accessType,
                    fileId,
                    alwaysCheckMFA,
                });

                const status = (result as any).status;
                const hasViewUrl = !!(result as any).viewUrl;
                const hasLink = !!(result as any).link;

                if (status === 'success' || hasViewUrl || hasLink) {
                    this.clearPollingInterval(fileId);
                    this.updateFileAccessState(fileId, accessType, {
                        MFAStatePartial: { ...MFA_STATE_INIT },
                        newAccessTypeState: { result, isLoading: false },
                    });
                } else if (status !== 'processing' && status !== 'protected') {
                    this.clearPollingInterval(fileId);
                    const fallbackState = this.accessMap.get(fileId)?.accessTypesState?.[accessType];
                    this.updateFileAccessState(fileId, accessType, {
                        MFAStatePartial: { ...MFA_STATE_INIT },
                        newAccessTypeState: {
                            ...(fallbackState && { ...fallbackState }),
                            isLoading: false,
                        },
                    });
                }
            } catch (error) {
                this.clearPollingInterval(fileId);
                const pollErrorType = getErrorType(error);
                const fallbackState = this.accessMap.get(fileId)?.accessTypesState?.[accessType];
                this.updateFileAccessState(fileId, accessType, {
                    MFAStatePartial: { ...MFA_STATE_INIT },
                    newAccessTypeState: {
                        ...(fallbackState && { ...fallbackState }),
                        errorType: pollErrorType,
                        isLoading: false,
                    },
                });
            }
        }, 2000);

        this.pollingIntervals.set(fileId, pollInterval);
    };

    tryFetchAccessLink = async ({
        fileId,
        filename,
        accessType,
        code,
        throwError = false,
        alwaysCheckMFA,
        isProtected,
    }: FetchFileArgs): Promise<boolean | never> => {
        return this.processRequest(
            fileId,
            accessType,
            !code,
            async (): Promise<void> => {
                const { API } = this.authSettingsStore;
                const result = await openFile<typeof accessType>({
                    API,
                    accessType,
                    fileId,
                    code,
                    alwaysCheckMFA,
                    isProtected,
                });
                this.updateFileAccessState(fileId, accessType, {
                    MFAStatePartial: { ...MFA_STATE_INIT },
                    newAccessTypeState: { result, isLoading: false },
                });

                const status = (result as SuccessAccessResponse).status;
                if (status === 'success' && accessType === OpenOptions.download) {
                    this.batchDownloadStore.uploadProtectedFileResult(
                        fileId,
                        filename || fileId,
                        result as DownloadActionResult
                    );
                    return;
                }

                if (status === 'protected' && accessType === OpenOptions.viewer) {
                    // A viewer open mapped to 'protected' comes from a 408 (file still being
                    // secured). Route it to the dedicated "Still securing file" screen — which
                    // owns the countdown and silent auto-retry — instead of leaving a blank
                    // viewer widget rendered from a result that has no viewUrl/link.
                    this.updateFileAccessState(fileId, accessType, {
                        MFAStatePartial: { ...MFA_STATE_INIT },
                        newAccessTypeState: { errorType: 'fileBeingPrepared', isLoading: false },
                    });
                    return;
                }

                if (status === 'processing' || status === 'protected') {
                    if (accessType === OpenOptions.viewer) {
                        this.startViewerPolling(fileId, filename, accessType, alwaysCheckMFA);
                    } else {
                        this.moveStatusCheckToBatchDownload(
                            result as DownloadActionResult,
                            fileId,
                            filename,
                            accessType
                        );
                    }
                }
            },
            throwError,
            filename,
        );
    };

    tryStartBatchDownload = async (
        filesIds: string[],
        code?: string,
        throwError = false,
    ): Promise<boolean | never> => this.processRequest(
        BATCH_DOWNLOAD_KEY,
        'batchDownload',
        !code,
        async (): Promise<void> => {
            await this.batchDownloadStore.startBatchDownload(filesIds, code);
            this.deleteAccessItem(BATCH_DOWNLOAD_KEY);
        },
        throwError,
    );

    private async processRequest(
        key: string,
        accessType: AccessType,
        isNewAttempt: boolean,
        worker: AsyncCallback<void>,
        throwError = false,
        filename: string = '',
    ): Promise<boolean | never> {
        this.processStart(key, accessType, isNewAttempt);
        let result = true;
        try {
            await worker();
        } catch (error) {
            this.processError(key, accessType, error, throwError, filename);
            result = false;
        }
        return result;
    }

    private processStart(key: string, accessType: AccessType, isNewAttempt: boolean): void {
        const initAccessState: AccessTypeState<AccessType> = { isLoading: true };
        // Reset MFAPassword context on every new request start so that
        // MFA password modal always relies on fresh data.
        this.setLastMFAPasswordContext(null);
        if (!this.accessMap.get(key)) {
            const initValue: FileAccessState = {
                MFAState: { ...MFA_STATE_INIT },
                accessTypesState: { [accessType]: initAccessState },
            };
            this.setAccessItem(key, initValue);
        } else if (isNewAttempt) {
            this.updateFileAccessState(key, accessType, {
                MFAStatePartial: { ...MFA_STATE_INIT },
                newAccessTypeState: initAccessState,
            });
        } else {
            this.updateFileAccessState(key, accessType, { newAccessTypeState: initAccessState });
        }
    }

    private updateFileAccessState(
        key: string,
        accessType: AccessType,
        {
            MFAStatePartial = {},
            newAccessTypeState,
        }: StatePartial,
    ): void {
        const itemState = this.accessMap.get(key);
        if (itemState) {
            const { accessTypesState, MFAState } = itemState;
            const newAccessTypesState: FullAccessState = newAccessTypeState
                ? { ...accessTypesState, [accessType]: newAccessTypeState }
                : accessTypesState;
            const newItemState: FileAccessState = {
                ...itemState,
                MFAState: { ...MFAState, ...MFAStatePartial },
                accessTypesState: newAccessTypesState,
            };
            this.setAccessItem(key, newItemState);
        }
    }

    private processError(
        key: string,
        accessType: AccessType,
        error: unknown,
        throwError = false,
        filename: string = '',
    ): void {
        let errorType = getErrorType(error);
        // Remember where this MFA error originated from so UI can
        // distinguish real batch-download flows from single-file flows.
        const isBatchKey = key === BATCH_DOWNLOAD_KEY;
        const currentState: FileAccessState = this.accessMap.get(key);
        if (currentState) {
            let { MFAState: { attemptsCounter } } = currentState;
            if (errorType === 'MFAFailed') {
                attemptsCounter -= 1;
                if (attemptsCounter === 0) {
                    errorType = 'MFALimitReached';
                }
            }
            // Only create entries in the downloads widget for real download/batch flows.
            const isDownloadContext = accessType === OpenOptions.download || accessType === 'batchDownload';
            if (errorType === 'MFARequired') {
                this.batchDownloadStore.setLastMFASource(isBatchKey ? 'batch' : 'single');
                this.batchDownloadStore.setIsMFAMounted(true);
                this.batchDownloadStore.setIsMFAVisible(true);
            }
            if (errorType === 'MFAPasswordRequired') {
                this.setLastMFAPasswordContext({ key, accessType });
                this.batchDownloadStore.setLastMFASource(isBatchKey ? 'batch' : 'single');
                this.batchDownloadStore.setIsMFAPasswordMounted(true);
                this.batchDownloadStore.setIsMFAPasswordVisible(true);
            }
            if (isDownloadContext) {
                this.batchDownloadStore.setMFARequiredFilesList([key, ...this.batchDownloadStore.MFARequiredFilesList]);
                this.batchDownloadStore.MFARequiredStatusUpdate(key, filename);
            }
            const errorState: StatePartial = {
                newAccessTypeState: { errorType, isLoading: false },
            };
            if (isUnionType<MFAError>(errorType, MFA_ERRORS)) {
                errorState.MFAStatePartial = { errorType, attemptsCounter };
            }
            this.updateFileAccessState(key, accessType, errorState);
            if (errorType === 'serverError') {
                captureErrorForSentry(error, 'FilesAccessStore.processError');
            }
            if (errorType === 'fileBeingPrepared') {
                captureErrorForSentry(error, 'FilesAccessStore.fileBeingPrepared');
            }
            // Update batch download store if this file is being downloaded
            if (this.batchDownloadStore.downloadsMap.has(key)) {
                const { statusCode, message: beMessage } = getErrorResponse(error);
                const isMissingProtectionPassword = statusCode === 409 && beMessage === 'Missing protection password';
                const errorMessage = isMissingProtectionPassword ? beMessage : getOpenFileErrorText(errorType, i18n.t);
                this.batchDownloadStore.updateBatchDownloadStatusWithError(key, filename || key, errorMessage);
            }
        }
        if (throwError) {
            throw error;
        }
    }

    private moveStatusCheckToBatchDownload(
        result: DownloadActionResult,
        fileId: string,
        filename: string,
        accessType: OpenOptions = OpenOptions.download,
    ): void {
        if (result.batchId) {
            this.batchDownloadStore.startBatchDownloadStatusCheck(result.batchId, filename || fileId);
        } else {
            if (result.status === 'protected') {
                this.batchDownloadStore.startSingleProtectedDownloadStatusCheck(fileId, filename || fileId, accessType);
            } else {
                this.batchDownloadStore.startSingleDownloadStatusCheck(fileId, filename || fileId, accessType);
            }
        }
    }
}

export default FilesAccessStore;
